import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { salesExpenseService } from '../../services/salesExpenseService';
import { formatCurrency } from '../../components/CurrencyTextField';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  EXPENSE_STATUS_META,
  SalesCard,
  SalesPageHeader,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
} from '../../components/sales/salesUi';

/** Prevent Strict Mode / remounts from approving the same expense twice from ?approve=1. */
const approveInFlight = new Set();

const SalesExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expense, setExpense] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updating, setUpdating] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesExpenseService.getById(id);
      setExpense(res.data);
      setCanApprove(Boolean(res.can_approve));
      return res;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load expense');
      setExpense(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Email APPROVE button → /sales/expenses/:id?approve=1 (requires login + supervisor/manager)
  useEffect(() => {
    if (loading) return;
    const wantApprove = searchParams.get('approve') === '1';
    if (!wantApprove) return;

    const expenseId = parseInt(id, 10);
    if (!Number.isFinite(expenseId) || approveInFlight.has(expenseId)) return;
    approveInFlight.add(expenseId);

    const next = new URLSearchParams(searchParams);
    next.delete('approve');
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        setError('');
        setSuccess('');
        setUpdating(true);
        const res = await salesExpenseService.getById(id);
        const row = res?.data;
        const allowed = Boolean(res?.can_approve);
        setCanApprove(allowed);

        if (!row) {
          setError(`Expense #${expenseId} was not found.`);
          return;
        }
        if (!allowed) {
          setExpense(row);
          setError('You do not have permission to approve this expense.');
          return;
        }
        if (row.status === 'approved' || row.status === 'paid') {
          setExpense(row);
          setSuccess(
            row.status === 'paid'
              ? `Expense #${expenseId} is already marked Paid.`
              : `Expense #${expenseId} is already approved.`
          );
          return;
        }
        if (row.status !== 'pending') {
          setExpense(row);
          setError(`Expense #${expenseId} is currently "${row.status}" and cannot be approved from this link.`);
          return;
        }

        const updated = await salesExpenseService.updateStatus(id, 'approved');
        setExpense(updated.data);
        setCanApprove(true);
        setSuccess(
          `Expense #${expenseId} approved. The Expense Reimbursement notification list has been emailed so they can mark it REIMBURSED.`
        );
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to approve expense');
        await load();
      } finally {
        setUpdating(false);
      }
    })();
    // Run once after initial load for inbound ?approve=1 links (mirrors HR markPaid).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    let revoked = false;
    let objectUrl = '';
    (async () => {
      if (!expense?.id) {
        setImageUrl('');
        return;
      }
      try {
        const response = await axios.get(salesExpenseService.getDownloadUrl(expense.id), {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(response.data);
        if (!revoked) setImageUrl(objectUrl);
      } catch {
        if (!revoked) setImageUrl('');
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [expense?.id]);

  const submitterName = expense
    ? [expense.first_name, expense.last_name].filter(Boolean).join(' ').trim() || '—'
    : '—';

  const reviewerName = expense
    ? [expense.reviewed_by_first_name, expense.reviewed_by_last_name].filter(Boolean).join(' ').trim()
    : '';

  const handleStatus = async (status) => {
    setUpdating(true);
    setError('');
    setSuccess('');
    try {
      const res = await salesExpenseService.updateStatus(id, status);
      setExpense(res.data);
      setCanApprove(true);
      if (status === 'approved') {
        setSuccess(
          'Expense approved. The Expense Reimbursement notification list has been emailed so they can mark it REIMBURSED.'
        );
      } else {
        setSuccess(`Status updated to ${status}.`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!expense) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Expense not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/expenses')}>
          Back to expenses
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <SalesPageHeader
        title={`Expense #${expense.id}`}
        subtitle="Receipt details and approval status"
        actions={
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/sales/expenses')}
            sx={{ textTransform: 'none', color: SALES.ink }}
          >
            Back to expenses
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <SalesCard>
          <Typography variant="subtitle2" sx={{ color: SALES.muted, mb: 1.5 }}>
            Details
          </Typography>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <SalesStatusChip status={expense.status} metaMap={EXPENSE_STATUS_META} />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Submitted by</Typography>
              <Typography variant="body2">{submitterName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Submitted</Typography>
              <Typography variant="body2">
                {expense.submitted_at ? formatDateDisplay(expense.submitted_at) : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Receipt date</Typography>
              <Typography variant="body2">
                {expense.receipt_date ? formatDateDisplay(expense.receipt_date) : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography variant="body2">
                {expense.amount != null ? formatCurrency(expense.amount) : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Job/Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {expense.notes || '—'}
              </Typography>
            </Box>
            {reviewerName && (
              <Box>
                <Typography variant="caption" color="text.secondary">Reviewed by</Typography>
                <Typography variant="body2">
                  {reviewerName}
                  {expense.reviewed_at ? ` · ${formatDateDisplay(expense.reviewed_at)}` : ''}
                </Typography>
              </Box>
            )}
          </Stack>

          {canApprove && (
            <Stack direction="row" spacing={1} sx={{ mt: 2.5 }} flexWrap="wrap" useFlexGap>
              {expense.status === 'pending' && (
                <>
                  <Button
                    variant="contained"
                    disabled={updating}
                    onClick={() => handleStatus('approved')}
                    sx={salesPrimaryButtonSx}
                  >
                    Approve
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={updating}
                    onClick={() => handleStatus('rejected')}
                  >
                    Reject
                  </Button>
                </>
              )}
              {expense.status === 'approved' && (
                <Button
                  variant="contained"
                  disabled={updating}
                  onClick={() => handleStatus('paid')}
                  sx={salesPrimaryButtonSx}
                >
                  Mark paid
                </Button>
              )}
              {expense.status !== 'pending' && (
                <Button
                  variant="outlined"
                  disabled={updating}
                  onClick={() => handleStatus('pending')}
                  sx={{ textTransform: 'none' }}
                >
                  Reopen as pending
                </Button>
              )}
            </Stack>
          )}
        </SalesCard>

        <SalesCard>
          <Typography variant="subtitle2" sx={{ color: SALES.muted, mb: 1.5 }}>
            Receipt photo
          </Typography>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Receipt"
              sx={{
                width: '100%',
                maxHeight: 520,
                objectFit: 'contain',
                borderRadius: 1,
                bgcolor: '#F8FAFC',
                border: `1px solid ${SALES.line}`,
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Receipt image unavailable.
            </Typography>
          )}
        </SalesCard>
      </Box>
    </Box>
  );
};

export default SalesExpenseDetail;
